<template>
  <VeeForm class="return-case-form-cnt" as="" ref="form" :validation-schema="validationSchema">
    <div class="return-case-form">
      <div class="customer-info">
        <BackstageInputLabel :label="$t('viewCase.return_reason')" required :class="{ disabled: !caseNotSubmitted }">
          <VeeField
            as="select"
            v-model="updatedPickupDetails.returnReason"
            :disabled="!caseNotSubmitted"
            name="return_reason"
            :class="{
              'with-arrow': caseNotSubmitted,
              'option-placeholder': updatedPickupDetails.returnReason === '',
            }"
            data-cy="defect-reason-dropdown"
            v-on:change="updatePickUpData">
            <option :value="null" disabled selected hidden>
              {{ $t('global.select_placeholder') }}
            </option>
            <option v-for="defectReason in defectReasons" :key="defectReason.type" :value="defectReason.type">
              {{ getTranslationFromTranslateField(defectReason.description) }}
            </option>
          </VeeField>
        </BackstageInputLabel>

        <BackstageInput
          v-model="updatedPickupDetails.description"
          :label="$t('returnCaseForm.specialInstructions')"
          type="text"
          :max-chars="1000"
          name="special_instructions"
          :disabled="!caseNotSubmitted"
          v-on:change="updatePickUpData">
        </BackstageInput>

        <div class="address-info">
          <h5 class="title">
            {{ $t('returnCaseForm.pickupFrom') }}
          </h5>
          <div class="radio-new" :class="[{ disabled: !!oneTimeAddress || !caseNotSubmitted }]">
            <input v-model="selectedAddress" value="default" type="radio" name="address" :disabled="!!oneTimeAddress" />
            <label for="default">{{ $t('viewOrder.default_address') }}</label>
          </div>
          <div v-if="!oneTimeAddress" class="or">
            <span>{{ $t('viewOrder.default_or') }}</span>
          </div>
          <BackstageButton
            v-if="!oneTimeAddress"
            :variant="ButtonVariant.SECONDARY"
            full-width
            :disabled="!caseNotSubmitted"
            v-on:click="toggleEditAddress()">
            {{ $t('returnCaseForm.pickUpFromAddress') }}
          </BackstageButton>
          <div v-if="oneTimeAddress" class="radio-new" :class="[{ disabled: !caseNotSubmitted }]">
            <input v-model="selectedAddress" value="oneTimeAddress" type="radio" name="address" />
            <label v-if="oneTimeAddress" v-html="oneTimeAddress"></label>
            <div v-if="caseNotSubmitted" class="one-time-address-actions">
              <BackstageIcon class="icon" name="edit" :disabled="!caseNotSubmitted" v-on:click="toggleEditAddress()">
              </BackstageIcon>
              <BackstageIcon
                class="icon"
                name="trash"
                :disabled="!caseNotSubmitted"
                v-on:click="removeDeliveryAddress()">
              </BackstageIcon>
            </div>
          </div>
        </div>

        <BackstageInput
          name="address_notes"
          type="text"
          :max-chars="50"
          :disabled="!caseNotSubmitted"
          v-on:change="updatePickUpData"
          :label="$t('returnCaseForm.addressNotes')"
          v-model="updatedPickupDetails.addressNotes">
        </BackstageInput>

        <BackstageInput
          v-model="updatedPickupDetails.contact.name"
          :label="$t('returnCaseForm.contactName')"
          :required="caseNotSubmitted"
          :disabled="!caseNotSubmitted"
          type="text"
          :max-chars="50"
          name="contact_name"
          v-on:change="updatePickUpData">
        </BackstageInput>

        <div class="phone-number-cnt double-fields">
          <VeeField
            as="select"
            v-model="callingCountryCode"
            class="phone-code with-arrow"
            name="phone_number_country_code"
            :class="[{ disabled: !caseNotSubmitted }]"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
            <option
              v-for="callingCode in getCountryCallingCodes()"
              :key="callingCode.isoCode3"
              :value="getFormattedCountryCode(callingCode.countryCodes[0], callingCode.country)">
              {{ `+${callingCode.countryCodes} ${callingCode.country}` }}
            </option>
          </VeeField>
          <BackstageInput
            v-model="updatedPickupDetails.contact.phone"
            :disabled="!caseNotSubmitted"
            :label="$t('returnCaseForm.phoneNumber')"
            :required="caseNotSubmitted"
            name="phone_number"
            type="number"
            class="phone-number number-field"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <BackstageInput
          v-model="updatedPickupDetails.contact.email"
          :disabled="!caseNotSubmitted"
          :label="$t('returnCaseForm.consigneeEmail')"
          :required="caseNotSubmitted"
          name="contact_email"
          type="text"
          v-on:change="updatePickUpData">
        </BackstageInput>

        <BackstageInput
          v-model="updatedPickupDetails.customerReference"
          :label="$t('returnCaseForm.customerReference')"
          type="text"
          :max-chars="50"
          name="customer_reference"
          :disabled="!caseNotSubmitted"
          v-on:change="updatePickUpData">
        </BackstageInput>
      </div>
      <div class="products-info">
        <BackstageInputLabel
          :label="$t('returnCaseForm.locationRestricted')"
          :class="[{ disabled: !caseNotSubmitted }]">
          <VeeField
            as="select"
            v-model="updatedPickupDetails.restrictedAccess"
            name="location_restricted"
            class="location-restricted-select with-arrow"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
            <option v-for="option in getYesNoOptions()" :key="option" :value="option === yesOption">
              {{ $t(`returnCaseForm.${option}`) }}
            </option>
          </VeeField>
        </BackstageInputLabel>

        <BackstageInputLabel :label="$t('returnCaseForm.tailLift')" :class="[{ disabled: !caseNotSubmitted }]">
          <VeeField
            as="select"
            v-model="updatedPickupDetails.tailLiftRequired"
            name="tail_lift"
            class="tail-lift with-arrow"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
            <option v-for="option in getYesNoOptions()" :key="option" :value="option === yesOption">
              {{ $t(`returnCaseForm.${option}`) }}
            </option>
          </VeeField>
        </BackstageInputLabel>

        <BackstageInputCalendar
          v-model="pickupDate"
          class="date-selector"
          name="collection_date"
          :label="$t('returnCaseForm.collectionDate')"
          :min-date="new Date()"
          :placeholder="$t('global.datepicker_placeholder')"
          :disabled="!caseNotSubmitted"
          v-on:update:modelValue="updatePickUpData()">
          <template #tooltip>
            <span class="has-tooltip" data-cy="tooltip-production-code">
              ¡
              <p class="tooltip">{{ $t('returnCaseForm.datepickerTooltip') }}</p>
            </span>
          </template>
        </BackstageInputCalendar>

        <div class="flex-grid">
          <BackstageInput
            v-model="updatedPickupDetails.parcelCount"
            :disabled="!caseNotSubmitted"
            :label="$t('returnCaseForm.parcels')"
            :required="caseNotSubmitted"
            type="number"
            name="parcels"
            class="flex-grow"
            v-on:change="updatePickUpData">
          </BackstageInput>

          <BackstageInput
            v-model="updatedPickupDetails.palletCount"
            :disabled="!caseNotSubmitted"
            :label="$t('returnCaseForm.pallets')"
            :required="caseNotSubmitted"
            type="number"
            name="pallets"
            class="flex-grow"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <span v-show="!hasParcelOrPallet" class="validation-error">
          {{ $t('formsValidations.fields.one_pallet_or_parcel') }}
        </span>

        <div class="double-fields">
          <BackstageInputLabel
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            :class="[{ disabled: !caseNotSubmitted }]">
            <VeeField
              as="select"
              v-model="updatedPickupDetails.weightUnit"
              class="with-arrow"
              name="weight_unit"
              :disabled="!caseNotSubmitted"
              v-on:change="updatePickUpData">
              <option v-for="weightUnit in getWeightUnits()" :key="weightUnit" :value="weightUnit">
                {{ $t(`returnCaseForm.weightUnits.${weightUnit}`) }}
              </option>
            </VeeField>
          </BackstageInputLabel>
          <BackstageInput
            v-model.number="updatedPickupDetails.totalPackageWeight"
            :label="$t('returnCaseForm.parcelsWeight')"
            type="number"
            name="parcels_total_weight"
            class="number-field"
            :required="caseNotSubmitted"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <div class="double-fields">
          <BackstageInputLabel
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            :class="[{ disabled: !caseNotSubmitted }]">
            <select
              v-model="updatedPickupDetails.weightUnit"
              class="with-arrow"
              name="weight_unit"
              :disabled="!caseNotSubmitted"
              v-on:change="updatePickUpData">
              <option v-for="weightUnit in getWeightUnits()" :key="weightUnit" :value="weightUnit">
                {{ $t(`returnCaseForm.weightUnits.${weightUnit}`) }}
              </option>
            </select>
          </BackstageInputLabel>
          <BackstageInput
            v-model.number="updatedPickupDetails.singlePackageWeight"
            :label="$t('returnCaseForm.individualParcelWeight')"
            type="number"
            name="individual_parcel_weight"
            class="number-field"
            :required="caseNotSubmitted"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <div class="double-fields">
          <BackstageInputLabel
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            :class="[{ disabled: !caseNotSubmitted }]">
            <select
              v-model="updatedPickupDetails.dimensions.unit"
              class="with-arrow"
              name="dimensions_unit"
              :disabled="!caseNotSubmitted"
              v-on:change="updatePickUpData">
              <option v-for="lengthUnit in getLengthUnits()" :key="lengthUnit" :value="lengthUnit">
                {{ $t(`returnCaseForm.lengthUnits.${lengthUnit}`) }}
              </option>
            </select>
          </BackstageInputLabel>
          <BackstageInput
            v-model.number="updatedPickupDetails.dimensions.width"
            :label="$t('returnCaseForm.parcelWidth')"
            type="number"
            name="parcel_width"
            class="number-field"
            :required="caseNotSubmitted"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <div class="double-fields">
          <BackstageInputLabel
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            :class="[{ disabled: !caseNotSubmitted }]">
            <select
              v-model="updatedPickupDetails.dimensions.unit"
              class="with-arrow"
              name="dimensions_unit"
              :disabled="!caseNotSubmitted"
              v-on:change="updatePickUpData">
              <option v-for="lengthUnit in getLengthUnits()" :key="lengthUnit" :value="lengthUnit">
                {{ $t(`returnCaseForm.lengthUnits.${lengthUnit}`) }}
              </option>
            </select>
          </BackstageInputLabel>
          <BackstageInput
            v-model.number="updatedPickupDetails.dimensions.height"
            :label="$t('returnCaseForm.parcelHeight')"
            type="number"
            name="parcel_height"
            class="number-field"
            :required="caseNotSubmitted"
            :disabled="!caseNotSubmitted"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>

        <div class="double-fields">
          <BackstageInputLabel
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            :class="[{ disabled: !caseNotSubmitted }]">
            <VeeField
              as="select"
              v-model="updatedPickupDetails.dimensions.unit"
              class="with-arrow"
              name="dimensions_unit"
              :disabled="!caseNotSubmitted"
              v-on:change="updatePickUpData">
              <option v-for="lengthUnit in getLengthUnits()" :key="lengthUnit" :value="lengthUnit">
                {{ $t(`returnCaseForm.lengthUnits.${lengthUnit}`) }}
              </option>
            </VeeField>
          </BackstageInputLabel>
          <BackstageInput
            v-model.number="updatedPickupDetails.dimensions.length"
            :label="$t('returnCaseForm.parcelLength')"
            :required="caseNotSubmitted"
            :disabled="!caseNotSubmitted"
            type="number"
            name="parcel_length"
            class="number-field"
            v-on:change="updatePickUpData">
          </BackstageInput>
        </div>
      </div>
    </div>
    <div v-if="caseNotSubmitted" class="responsibility-checkbox-cnt">
      <input
        id="responsibility-checkbox"
        v-model="responsibilityAcceptance"
        type="checkbox"
        v-on:change="updatePickUpData" />
      <label for="responsibility-checkbox">{{ $t('returnCaseForm.responsibilityMessage') }}</label>
    </div>
    <span v-show="!responsibilityAcceptance" class="validation-error">
      {{ $t('formsValidations.fields.responsibility_acceptance') }}
    </span>
    <NotificationMessage
      v-if="showEditAddressForm"
      :title="$t('viewOrder.one_time_address_title')"
      :size="'small'"
      v-on:close="toggleEditAddress()">
      <AddEditDeliveryAddress
        :delivery-address="deliveryAddress"
        :is-pickup-address="true"
        v-on:cancel="toggleEditAddress()"
        v-on:delivery-address-updated="updateDeliveryAddress">
      </AddEditDeliveryAddress>
    </NotificationMessage>
  </VeeForm>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import codes, { type ICountryCodeItem } from 'country-calling-code';
import BackstageInputLabel from '../common/BackstageInputLabel.vue';
import { measurementUnits } from '../../constants/measurements.contants';
import { CasePickupModel, YesNoOptions } from '../../models/case-pickup.model';
import { DefectReason } from '../../models/defect-reason.model';
import { AddressType } from '@onrunning/backstage-models';
import { AccountAddress } from '../../models/account-address.model';
import { globalConstants } from '../../constants/global.constants';
import NotificationMessage from '../common/NotificationMessage.vue';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { PopUpSize } from '@/models/pop-up-size.enum';
import AddEditDeliveryAddress from '../orders/add-edit-delivery-address.vue';
import { Form as VeeForm, Field as VeeField } from 'vee-validate';
import BackstageInput from '@/components/common/BackstageInput.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import { type AnyObjectSchema, type ObjectShape, object } from 'yup';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useFormValidation } from '@/composables/form-validation';
import { useFormat } from '../../composables/format';

const defaultAddressValue = 'default';
const oneTimeAddressValue = 'oneTimeAddress';

export default defineComponent({
  name: 'return-case-form',
  components: {
    NotificationMessage,
    AddEditDeliveryAddress,
    BackstageInputLabel,
    BackstageInput,
    BackstageButton,
    BackstageIcon,
    BackstageInputCalendar,
    VeeForm,
    VeeField,
  },
  props: {
    caseNotSubmitted: {
      type: Boolean,
      default: false,
    },
    defectReasons: {
      type: Array<DefectReason>,
      required: true,
    },
    pickupDetails: {
      type: Object,
      required: false,
      default: new CasePickupModel(),
    },
  },
  setup() {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const {
      dateRequiredValidation,
      numberValidationRequiredMinMaxValue,
      numberValidationMaxValue,
      textMaxCharsValidation,
      textRequiredValidation,
      textRequiredMaxCharsValidation,
      textRequiredMinMaxCharsValidation,
      phoneNumberValidation,
      emailValidation,
    } = useFormValidation();
    const { getTranslationFromTranslateField } = useFormat();
    return {
      accountId: selectedAccountId,
      account: selectedAccount,
      dateRequiredValidation,
      numberValidationRequiredMinMaxValue,
      numberValidationMaxValue,
      textMaxCharsValidation,
      textRequiredValidation,
      textRequiredMaxCharsValidation,
      textRequiredMinMaxCharsValidation,
      phoneNumberValidation,
      emailValidation,
      getTranslationFromTranslateField,
      ButtonVariant,
    };
  },
  data() {
    return {
      deliveryAddress: new AccountAddress(),
      selectedAddress: defaultAddressValue,
      pickupDate: undefined as unknown as Date,
      editAddressButtonToggled: false,
      callingCountryCode: '',
      responsibilityAcceptance: false,
      updatedPickupDetails: new CasePickupModel(),
      isValidFormData: false,
      yesOption: YesNoOptions.yes,
      popUpSize: PopUpSize.Small,
      formIsValid: false,
    };
  },
  computed: {
    validationSchema(): AnyObjectSchema {
      const maxChars = 50;

      let schema: ObjectShape = {
        special_instructions: this.textMaxCharsValidation('comments', 1000),
        phone_number: this.textRequiredMaxCharsValidation('phone_number', 11).min(
          8,
          this.$t('formsValidations.rules_error_messages.min_value', {
            field: this.$t(`formsValidations.fields.phone_number`),
            min_value: 8,
          }),
        ),
        return_reason: this.textRequiredValidation('return_reason'),
        contact_name: this.textRequiredMaxCharsValidation('contact_name', maxChars),
        contact_email: this.emailValidation('consignee_email'),
        customer_reference: this.textMaxCharsValidation('customer_reference', maxChars),
        address_notes: this.textMaxCharsValidation(
          'address_notes',
          maxChars,
          this.$t('returnCaseForm.addressNotesErrorMessage', { max_chars: maxChars }),
        ),
        // collection_date: this.dateRequiredValidation(),
        parcels: this.numberValidationMaxValue('parcels', 199),
        pallets: this.numberValidationMaxValue('pallets', 199),
        parcel_height: this.numberValidationRequiredMinMaxValue('parcel_height', 0.1, 500000),
        parcel_width: this.numberValidationRequiredMinMaxValue('parcel_width', 0.1, 500000),
        parcel_length: this.numberValidationRequiredMinMaxValue('parcel_length', 0.1, 500000),
        parcels_total_weight: this.numberValidationRequiredMinMaxValue('parcels_total_weight', 0.1, 500000),
        individual_parcel_weight: this.numberValidationRequiredMinMaxValue('individual_parcel_weight', 0.1, 500000),
      };

      return object(schema);
    },
    oneTimeAddress(): string | undefined {
      const validAddress = this.isValidAddress(this.deliveryAddress);

      const result = validAddress
        ? `${this.deliveryAddress?.street}, ${this.deliveryAddress?.zipCode}<br/>${
            this.deliveryAddress?.state ? this.deliveryAddress?.state + ' - ' : ''
          }${this.deliveryAddress?.city} - ${this.deliveryAddress?.country}`
        : undefined;

      return result;
    },
    showEditAddressForm(): boolean {
      return this.editAddressButtonToggled && !!this.deliveryAddress;
    },
    hasParcelOrPallet(): boolean {
      return (this.updatedPickupDetails.parcelCount ?? 0) + (this.updatedPickupDetails.palletCount ?? 0) > 0;
    },
    hasPickupDate(): boolean {
      return !!this.updatedPickupDetails.pickupDate;
    },
    isValidSinglePackageWeight(): boolean {
      return this.updatedPickupDetails.singlePackageWeight <= this.updatedPickupDetails.totalPackageWeight;
    },
  },

  beforeMount() {
    this.updatedPickupDetails = this.pickupDetails as CasePickupModel;
    this.pickupDate = this.updatedPickupDetails.pickupDate || this.getEarliestPickupDate();
    this.responsibilityAcceptance = !this.caseNotSubmitted;
    this.callingCountryCode = this.getCallingCode();

    if (this.updatedPickupDetails.address && this.isValidAddress(this.updatedPickupDetails.address)) {
      this.deliveryAddress = this.updatedPickupDetails.address;
      this.selectedAddress = oneTimeAddressValue;
    }
  },

  watch: {
    addEditDeliveryAddress: {
      handler() {
        this.validateForm();
      },
      deep: true,
    },
    updatedPickupDetails: {
      deep: true,
      handler() {
        this.updatePickUpData();
      },
    },
  },
  methods: {
    getCountryCallingCodes() {
      return codes;
    },

    getCallingCode() {
      if (this.updatedPickupDetails.contact.countryCode) {
        // Drop the + and pre-select the country code from the list
        return this.getFormattedCountryCode(
          this.updatedPickupDetails.contact.countryCode.substring(1),
          this.updatedPickupDetails.contact.country,
        );
      }

      // Attempt to pre-select the phone county code wrt account billing country
      let accountCountryCode = codes.find(
        (country) => country.isoCode3 === this.account?.billingCountry?.toLowerCase(),
      );

      if (!accountCountryCode) {
        // Use USA as default country
        accountCountryCode = codes.find((country) => country.isoCode3 === 'USA') as ICountryCodeItem;
      }

      return this.getFormattedCountryCode(accountCountryCode.countryCodes[0], accountCountryCode.country);
    },
    getFormattedCountryCode(countryCode: string, countryName: string): string {
      // Make the value unique to avoid the collition between USA and Canada since both of them have the phone code value 1
      return `${countryCode}${globalConstants.countryCodeAndCountryDelimiter}${countryName}`;
    },
    getSplittedCountryCode(formattedCountryCode: string) {
      return formattedCountryCode ? formattedCountryCode.split(globalConstants.countryCodeAndCountryDelimiter) : [];
    },
    isValidAddress(address: AccountAddress) {
      return !!address.city && !!address.zipCode && !!address.street;
    },
    getEarliestPickupDate() {
      const today = new Date();
      const earliestPickupDate = new Date();

      earliestPickupDate.setDate(today.getDate() + globalConstants.earliestPickupDayOffset);

      return earliestPickupDate;
    },
    getNumberOfItemsArray() {
      const maxPalletsOrParcels = 11;

      return Array(maxPalletsOrParcels)
        .fill(0)
        .map((_, index) => index);
    },
    getLengthUnits() {
      return Object.values<string>(measurementUnits.length);
    },
    getWeightUnits() {
      return Object.values<string>(measurementUnits.weight);
    },
    getYesNoOptions() {
      return Object.values<string>(YesNoOptions);
    },
    toggleEditAddress() {
      if (!this.editAddressButtonToggled && !this.deliveryAddress?.street) {
        this.deliveryAddress = new AccountAddress();
        this.deliveryAddress.country = this.account.billingCountry;
      }

      this.editAddressButtonToggled = !this.editAddressButtonToggled;
    },
    updateDeliveryAddress(deliveryAddress_?: AccountAddress) {
      if (deliveryAddress_ && this.isValidAddress(deliveryAddress_)) {
        this.deliveryAddress = deliveryAddress_;
        this.selectedAddress = oneTimeAddressValue;

        this.updatedPickupDetails.address = {
          ...deliveryAddress_,
          addressType: AddressType.Pickup,
        } as AccountAddress;
      }

      this.toggleEditAddress();
      this.updatePickUpData();
    },
    removeDeliveryAddress() {
      this.deliveryAddress = new AccountAddress();
      this.selectedAddress = defaultAddressValue;
      this.updatedPickupDetails.address = {} as AccountAddress;

      this.updatePickUpData();
    },
    updatePickUpData() {
      // Trigger update only when Case is not submitted
      if (this.caseNotSubmitted) {
        const splittedCountryCode = this.getSplittedCountryCode(this.callingCountryCode);

        if (splittedCountryCode.length === 2) {
          this.updatedPickupDetails.contact.countryCode = `+${splittedCountryCode[0]}`;
          this.updatedPickupDetails.contact.country = splittedCountryCode[1];
        }

        this.updatedPickupDetails.description = this.pickupDetails.description;
        this.updatedPickupDetails.returnReason = this.pickupDetails.returnReason;
        this.updatedPickupDetails.pickupDate = this.pickupDate;

        this.validateData();
      }
    },
    async validateForm() {
      this.formIsValid = (await (this.$refs as any).form?.validate({ mode: 'silent' }))?.valid ?? false;
    },
    async validateData() {
      const formValidationResult = (await (this.$refs as any).form?.validate()) as any;
      // Ideally we need to only check if the last changed input is valid. However it's quite impossible to get the id or ref of the last changed input field.
      // Hence check any of the changed/touched input fields are invalid and avoid making the update API call.
      let areChangedFieldsInvalid = false;
      if (formValidationResult?.results && Object.keys(formValidationResult.results).length !== 0) {
        areChangedFieldsInvalid = Object.keys(formValidationResult?.results).some((result) => {
          const inputField = (formValidationResult as any)?.results[result];

          return inputField.changed && inputField.invalid;
        }) as any;
      }
      const isRequiredFieldsFilled = (this.$refs as any).form?.validate({ silent: true }) && this.hasPickupDate;
      const isFormValid =
        (formValidationResult?.valid ?? false) &&
        isRequiredFieldsFilled &&
        this.hasParcelOrPallet &&
        this.responsibilityAcceptance;

      this.$emit('form-is-valid', isFormValid, !areChangedFieldsInvalid, this.updatedPickupDetails);
    },
  },
});
</script>

<style lang="scss" scoped>
@mixin flex-cnt {
  display: flex;
  gap: 12px;
}

.return-case-form {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @include breakpoint(medium) {
    grid-template-columns: repeat(2, 1fr);
  }

  .customer-info {
    @include flex-cnt;
    flex-direction: column;

    .address-info {
      width: 100%;
      padding: 22px 0;

      .one-time-address-actions {
        display: inherit;
      }

      .title {
        font-size: 12px;
        text-align: left;
        margin: 0 0 12px;
        font-weight: 400;
        color: var(--primary-label-input-color);
      }

      .or {
        width: 100%;
        height: 1px;
        background-color: var(--border-inactive-color);
        position: relative;
        margin: 21px 0;

        span {
          display: block;
          padding: 0 10px;
          background: var(--primary-bg-color);
          color: var(--primary-text-remark-color);
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 12px;
        }
      }

      .radio-new {
        width: 100%;

        .icon,
        i.ico {
          width: 24px;
          height: 24px;
          cursor: pointer;
        }
      }

      .button-container {
        width: 100%;
      }
    }

    .phone-number-cnt {
      .phone-code {
        border-radius: 4px;
        background-color: var(--field-bg-color);
        background-position: calc(100% - 16px) center;
        padding: 9.5px 40px 9.5px 12px;
        border: none;
        width: 100px;
        height: 58px;
        font-weight: 500;
      }

      .phone-number {
        flex: 1;
      }
    }
  }

  .double-fields {
    @include flex-cnt;

    .unit-selector {
      width: 5.5rem;
    }

    .number-field {
      flex: 1;
    }
  }

  .with-arrow {
    width: 100%;
  }

  .products-info {
    @include flex-cnt;
    flex-direction: column;

    .date-selector {
      .has-tooltip {
        position: relative;
        display: inline-block;
        color: var(--secondary-text-color);
        background-color: var(--secondary-bg-color);
        border-radius: 50%;
        padding: 0;
        cursor: pointer;
        height: 0.9375rem;
        width: 0.9375rem;
        text-align: center;
        line-height: 0.875rem;
        text-indent: unset;

        &:hover {
          .tooltip {
            display: block;
          }
        }

        .tooltip {
          position: absolute;
          z-index: 9;
          bottom: 100%;
          left: 0;
          display: none;
          transform: translateX(-50%);
          box-shadow: 0 1.25rem 2.5rem -0.875rem rgba(0, 0, 0, 0.25);
          padding: 16px;
          background-color: var(--primary-bg-color);
          color: var(--primary-text-color);
          width: 200px;
        }
      }
    }
  }
}

.responsibility-checkbox-cnt {
  @include flex-cnt;
  align-items: center;
  margin-top: 20px;
  max-width: 820px;

  label {
    text-align: left;
    font-size: 14px;
    font-weight: 400;
  }

  input[type='checkbox'] {
    appearance: none;
    background-color: #fff;
    margin: 0;
    padding: 0 4px;
    width: 22px;
    height: 22px;
    border: 1px solid #000000;
    border-radius: 2px;
    display: grid;
    place-content: center;
  }

  input[type='checkbox']::before {
    content: '';
    width: 14px;
    height: 14px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    background-color: #000000;
    border-radius: 2px;
  }

  input[type='checkbox']:checked::before {
    transform: scale(1);
  }
}
</style>
